<template>
  <DataTableServer
    v-model="selectedItems"
    @click:row="handleRowClick"
    @update:page="handlePageChange"
    :headers="tableHeaders"
    :items="workflowInstances"
    :items-length="totalItemCount"
    :items-per-page="pageSize"
    :loading="processing"
    :page="page"
    :show-select="isSpecialist"
    no-data-text="No tasks found"
    border
    hover
  >
    <template
      v-if="workflowInstances.length"
      #filters
    >
      <div class="d-flex align-center ga-2">
        <StandardButton
          v-for="chip in workflowTypeChips"
          @click="handleWorkflowTypeClick(chip.statusFilter)"
          :key="chip.statusFilter"
          :custom-classes="workflowTypeFilterClasses(chip.statusFilter)"
          :loading="processing"
          :text="chip.typeLabel + ': ' + (workflowTypeCount(chip.statusFilter) || 0)"
          color=""
          variant="outlined"
          rounded
          tracked
        />

        <FilterMenu
          classes="d-none d-md-inline-flex h-100pc"
          color="white"
          tid="task_date_filter"
          title="Date"
          paddingless
          tile
        >
          <template #card>
            <v-list
              v-model:selected="sortOrder"
              @update:selected="loadWorkflowInstances"
            >
              <v-list-item
                title="Newest created first"
                value="desc"
              >
                <template #prepend="{ isSelected }">
                  <v-radio
                    :model-value="isSelected"
                    color="primary"
                    tabindex="-1"
                  />
                </template>
              </v-list-item>

              <v-list-item
                title="Oldest created first"
                value="asc"
              >
                <template #prepend="{ isSelected }">
                  <v-radio
                    :model-value="isSelected"
                    color="primary"
                    tabindex="-1"
                  />
                </template>
              </v-list-item>
            </v-list>
          </template>
        </FilterMenu>
      </div>

      <v-spacer />

      <StandardButton
        v-if="isSpecialist && selectedItems.length"
        disabled-text="Assign functionality not available yet"
        prepend-icon="person_add"
        text="Assign"
      />
    </template>

    <template #[`item.id`]="{ item: workflow }">
      {{ shortId(workflow.id) }}
    </template>

    <template #[`item.status`]="{ item: work }">
      <StandardChip
        :data-testid="`license-workflow-status-${work.id}`"
        :text="work.status"
        role="status"
        capitalize
      />
    </template>

    <template #[`item.date`]="{ item: work }">
      <LongDate :date="work.date" />
    </template>
  </DataTableServer>
</template>

<script setup>
import { useRoute, useRouter } from 'vue-router';

import DataTableServer from '@/shared/components/table/DataTableServer.vue';
import FilterMenu from '@/shared/components/form/FilterMenu.vue';
import LongDate from '@/shared/components/LongDate.vue';
import { shortId } from '@/plugins/filters';
import useApi from '@/shared/composables/useApi';
import useRole from '@/shared/composables/useRole';

const route = useRoute();
const router = useRouter();
const { scopedApi } = useApi();
const { isSpecialist, isManager } = useRole();

const filterStatus = ref('');
const page = ref(1);
const pageSize = ref(10);
const processing = ref(false);
const sortOrder = ref(['desc']);
const tableHeaders = ref([
  { title: 'ID', value: 'id' },
  { title: 'Title', value: 'title' },
  { title: 'Type', value: 'type' },
  { title: 'Status', value: 'status', align: 'center' },
  { title: 'Assignee', value: 'assignee' },
  { title: 'Date', value: 'date', align: 'end' },
]);
const totalItemCount = ref(1);

const selectedItems = ref([]);

const workflowInstances = ref([]);
const workflowTypeMetricsSnapshot = ref(null);
const workflowTypeMetricsLicenseId = ref(null);

const workflowInstancesForChipMetrics = computed(
  () => workflowTypeMetricsSnapshot.value ?? workflowInstances.value,
);

const workflowTypeChips = computed(() => {
  const merged = workflowInstancesForChipMetrics.value;
  const seen = new Set();
  const ordered = [];

  for (const row of merged) {
    if (!row.type) continue;

    const statusFilter = workflowTemplateNameStatusParam(row.type);
    if (!statusFilter) continue;
    if (seen.has(statusFilter)) continue;

    seen.add(statusFilter);
    ordered.push({
      statusFilter,
      typeLabel: row.type.trim(),
    });
  }

  return ordered.sort((left, right) =>
    left.typeLabel.localeCompare(right.typeLabel, undefined, { sensitivity: 'base' }),
  );
});

function handlePageChange(newPage) {
  page.value = newPage;
  void loadWorkflowInstances();
}

async function handleRowClick(_event, { item: workflowInstance }) {
  const licenseRouteId = route.params.licenseId ?? route.params.id;

  if (isSpecialist) {
    await router.push({
      name: 'LicenseWorkflowInstanceShow',
      params: {
        licenseId: licenseRouteId,
        workflowInstanceId: workflowInstance.id,
      },
    });
    return;
  }

  if (isManager) {
    await router.push({
      name: 'WorkflowInstanceShow',
      params: { workflowInstanceId: workflowInstance.id },
      query: { license_id: licenseRouteId },
    });
    return;
  }
}

function handleWorkflowTypeClick(workflowType) {
  if (filterStatus.value === workflowType) {
    filterStatus.value = '';
  } else {
    filterStatus.value = workflowType;
  }

  void loadWorkflowInstances();
}

async function load() {
  await loadWorkflowInstances();
}

async function loadWorkflowInstances() {
  if (!scopedApi) {
    workflowInstances.value = [];
    workflowTypeMetricsSnapshot.value = null;
    return;
  }

  processing.value = true;

  try {
    const licenseRouteId = route.params.licenseId ?? route.params.id;

    if (workflowTypeMetricsLicenseId.value !== licenseRouteId) {
      workflowTypeMetricsLicenseId.value = licenseRouteId;
      workflowTypeMetricsSnapshot.value = null;
    }

    const params = {
      license_id: licenseRouteId,
      sort_order: workflowInstanceListSortOrder(sortOrder.value),
    };

    if (filterStatus.value) params.status = filterStatus.value;

    const response = await scopedApi.workflowInstance.index(params, null, () => null);

    const rows = response?.data ?? [];
    workflowInstances.value = rows;

    if (!filterStatus.value) workflowTypeMetricsSnapshot.value = rows;
  } finally {
    processing.value = false;
  }
}

function workflowInstanceListSortOrder(selected) {
  const token = Array.isArray(selected) ? selected[0] : selected;
  return token === 'asc' ? 'asc' : 'desc';
}

function workflowTemplateNameStatusParam(rowType) {
  return rowType.trim().toLowerCase();
}

function workflowTypeCount(workflowType) {
  return workflowInstancesForChipMetrics.value.filter(
    (item) => workflowTemplateNameStatusParam(item.type) === workflowType,
  ).length;
}

function workflowTypeFilterClasses(statusFilter) {
  return [
    'c-black fs-13 fs-md-14 fw-500',
    filterStatus.value === statusFilter
      ? 'bc-primary-light bg-primary-extra-light'
      : 'bc-very-light-gray filter-chip-inactive',
  ];
}

onMounted(load);
</script>
